import { useState } from "react";
import { useDispatch } from "react-redux";
import Stripe from "components/Stripe";
import {
  getBillingKeyInfo,
  initiatePayment,
  getInvoiceDetails,
} from "store/actions";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentHeader from "components/PaymentHeader";
import { getEnv, getStripeKey } from "components/helper";
import useQuery from "components/customHooks/useQuery";
import useOnMount from "components/customHooks/useOnMount";
import { Skeleton, FormControlLabel, Radio } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import PaymentChargeEntryTable from "components/PaymentChargeEntryTable";
import { prodRedirectKeys } from "components/prodRedirectKeys";

const getInvoicePaymentType = (all_invoices, selected_invoices) => {
  const selected = new Set(selected_invoices);
  for (let invoice of all_invoices) {
    if (invoice.status === "Bill Paid") continue;
    if (!selected.has(invoice.charge_entry_id)) return "partial";
  }
  return "full";
};

const stripePromise = loadStripe(getStripeKey());

const initialError = {
  zip: false,
  amt: false,
  invoice: false,
  last_name: false,
};
/* 
  * params
  @src : spry or null => shows spry logo or shows orciare logo respectively
  @key : Key to indentify invoice
*/
const PaymentPage = () => {
  const [formDetails, setFormDetails] = useState({
    zip: "",
    amt: "",
    invoice: "",
    last_name: "",
    practice_id: "",
    patient_id: "",
    invoice_details: [],
  });
  const [tractsact_fees, setTractsact_fees] = useState(0);
  const [isCompletePay, setIsCompletePay] = useState(true);
  const [selChargeEntries, setSelChargeEntries] = useState([]);
  const [clientSecret, setClientSecret] = useState("");
  // Loaders
  const [loading, setLoading] = useState(false); // Loader while fetching invoice details
  const [submitLoading, setSubmitLoading] = useState(false); // loader for submit
  // For General Error message
  const [errorMessage, setErrorMessage] = useState("");
  // For form validatations
  const [error, setError] = useState({ ...initialError });
  // Error when invoice details fails
  const [keyError, setKeyError] = useState(false);

  const dispatch = useDispatch();
  const query = useQuery();
  const key = query.get("key");

  const options = { clientSecret };
  // disable invoice and amount when key is available
  const readOnlyInput = Boolean(key);
  // Checks if atleast one charge entry is selected
  const noCESelected = key && selChargeEntries.length === 0;
  const isDisabled = keyError || loading || noCESelected;
  const [paymentLogo, setPaymentLogo] = useState("");

  const calculated_cost = formDetails?.invoice_details?.reduce((prev, curr) => {
    if (selChargeEntries.includes(curr.charge_entry_id)) {
      return prev + parseFloat(curr.total_pending_balance);
    }
    return prev;
  }, 0);

  useOnMount(() => {
    getInoviceDetails();
  });

  const getInoviceDetails = async () => {
    if (prodRedirectKeys.includes(key) && getEnv() !== "prod") {
      const src = query.get("src");
      let source_key = "";
      if (src) source_key = `src=${src}&`;
      window.location.replace(
        `https://billing-web.services.orcicare.com/payment?${source_key}key=${key}`
      );
    } else if (key) {
      setLoading(true);
      // * Validate key and get invoice details
      const response = await dispatch(getBillingKeyInfo({ key }));
      if (!response) {
        setKeyError(true);
        setErrorMessage("The web page you are trying to access is invalid.");
        setLoading(false);
        return;
      }
      const {
        bill_account_number,
        billing_amount,
        error,
        practice_id,
        patient_id,
        transaction_fees,
        bill_logo_name = "",
      } = response;
      setTractsact_fees(transaction_fees);
      setPaymentLogo(bill_logo_name);
      if (error) {
        setErrorMessage("The web page you are trying to access is invalid.");
        setKeyError(true);
        setLoading(false);
        return;
      }
      // * Fetches Invoice Details
      const invoice_details = await dispatch(
        getInvoiceDetails({
          bill_account_number,
          practice_id,
        })
      );
      if (!invoice_details) {
        setErrorMessage(
          "Error getting invoice details. Please try again later."
        );
        setKeyError(true);
      } else if (!Array.isArray(invoice_details)) {
        setErrorMessage("The web page you are trying to access is invalid.");
        setKeyError(true);
      } else {
        setIsCompletePay(true);
        setSelChargeEntries(
          invoice_details?.reduce((acc, curr) => {
            if (curr.status === "Bill Paid") return acc;
            acc.push(curr.charge_entry_id);
            return acc;
          }, [])
        );
        setFormDetails((prev) => ({
          ...prev,
          invoice: bill_account_number,
          amt: billing_amount + transaction_fees,
          amt_ref: billing_amount,
          invoice_details,
          practice_id,
          patient_id,
        }));
      }
      setLoading(false);
    }
  };

  const changePay = (value, type) => {
    setFormDetails((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const validate = () => {
    let isValid = true;
    let tempError = { ...initialError };
    if (formDetails.amt === "") {
      tempError.amt = true;
      isValid = false;
    }
    if (formDetails.invoice === "") {
      tempError.invoice = true;
      isValid = false;
    }
    if (formDetails.zip === "") {
      tempError.zip = true;
      isValid = false;
    }
    setError(tempError);
    return isValid;
  };

  const onCheckout = async (e) => {
    e.preventDefault();
    if (submitLoading) return;
    const isValid = validate();
    if (!isValid) return;
    setSubmitLoading(true);

    var payload = {};

    if (key) {
      payload = {
        zip: formDetails.zip,
        amt: parseFloat(calculated_cost).toFixed(2),
        invoice_number: formDetails.invoice,
      };
    } else {
      payload = {
        zip: formDetails.zip,
        amt: formDetails.amt,
        last_name: formDetails.last_name,
        invoice_number: formDetails.invoice,
      };
    }

    if (key) {
      payload.partial_paid_ce_list = selChargeEntries;
      payload["invoice_payment_type"] = getInvoicePaymentType(
        formDetails?.invoice_details,
        selChargeEntries
      );
    }
    const response = await dispatch(initiatePayment(payload));
    if (response?.success) {
      setClientSecret(response.client_secret);
    } else if (response?.error) {
      setErrorMessage(response?.status || "Please check details!");
    }
    setSubmitLoading(false);
  };

  return (
    <div className="payment-page-main">
      <PaymentHeader setFormDetails={setFormDetails} billLogo={paymentLogo} />
      <div className="payment-container">
        <main className="main">
          {clientSecret ? null : (
            <form className="checkout-form">
              <div className="pay-input-group">
                <label>Invoice ID</label>
                {loading ? (
                  <Skeleton style={{ height: 38, flex: 1 }} />
                ) : (
                  <input
                    value={formDetails.invoice}
                    onChange={(e) => changePay(e.target.value, "invoice")}
                    type="text"
                    name="invoice"
                    className={error.invoice ? "input input-error" : "input"}
                    placeholder="please enter invoice ID"
                    readOnly={readOnlyInput}
                    disabled={readOnlyInput}
                  />
                )}
              </div>
              <div className="pay-input-group">
                <label>Amount Due</label>

                {loading ? (
                  <Skeleton style={{ height: 38, flex: 1 }} />
                ) : (
                  <input
                    value={formDetails.amt}
                    onChange={(e) => changePay(e.target.value, "amt")}
                    type="number"
                    className={error.amt ? "input input-error" : "input"}
                    placeholder="Amount due"
                    readOnly={readOnlyInput}
                    disabled={readOnlyInput}
                  />
                )}
              </div>
              <div className="pay-input-group">
                <label>Last Name</label>
                <input
                  value={formDetails.last_name}
                  onChange={(e) => changePay(e.target.value, "last_name")}
                  className={error.last_name ? "input input-error" : "input"}
                  placeholder="Last Name"
                />
              </div>
              <div className="pay-input-group">
                <label>Zip Code</label>
                <input
                  value={formDetails.zip}
                  onChange={(e) => changePay(e.target.value, "zip")}
                  type="number"
                  className={error.zip ? "input input-error" : "input"}
                  placeholder="Zip Code"
                />
              </div>
              {key && formDetails?.invoice_details?.length > 0 ? (
                <>
                  {formDetails?.invoice_details?.length === 1 ? null : (
                    <div>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isCompletePay}
                            onChange={() => {
                              setIsCompletePay((prev) => !prev);
                              setSelChargeEntries(
                                formDetails?.invoice_details?.reduce(
                                  (acc, curr) => {
                                    if (curr.status === "Bill Paid") return acc;
                                    acc.push(curr.charge_entry_id);
                                    return acc;
                                  },
                                  []
                                )
                              );
                            }}
                            disabled={
                              formDetails?.invoice_details?.length === 1
                            }
                          />
                        }
                        label="Pay Completely"
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={!isCompletePay}
                            onChange={() => {
                              setIsCompletePay((prev) => !prev);
                            }}
                            disabled={
                              formDetails?.invoice_details?.length === 1
                            }
                          />
                        }
                        label="Pay Partially"
                      />
                    </div>
                  )}
                  <PaymentChargeEntryTable
                    invoice_details={formDetails?.invoice_details}
                    selChargeEntries={selChargeEntries}
                    setSelChargeEntries={setSelChargeEntries}
                    calculated_cost={calculated_cost}
                    isCompletePay={isCompletePay}
                    totalAmount={formDetails.amt}
                    transaction_fees={tractsact_fees}
                    amtData={formDetails.amt_ref}
                  />
                </>
              ) : null}
              {errorMessage ? (
                <p className="error-msg">{errorMessage}</p>
              ) : null}
              {!loading && !keyError && noCESelected ? (
                <p className="error-msg">Select atleast one charge entry</p>
              ) : null}
              <button
                onClick={onCheckout}
                className={`checkout-btn ${isDisabled ? "disabled" : ""}`}
                tabIndex="0"
                disabled={isDisabled}
              >
                {submitLoading ? (
                  <CircularProgress size={20} style={{ color: "#fff" }} />
                ) : (
                  "Checkout Now"
                )}
              </button>
            </form>
          )}
          {clientSecret ? (
            <Elements stripe={stripePromise} options={options}>
              <div className="payContainer">
                <Stripe
                  invoice_number={formDetails.invoice}
                  setClientSecret={setClientSecret}
                  isKeyAvailable={key}
                  partial_paid_ce_list={selChargeEntries}
                  invoice_payment_type={getInvoicePaymentType(
                    formDetails?.invoice_details,
                    selChargeEntries
                  )}
                  practice_id={formDetails.practice_id}
                  patient_id={formDetails.patient_id}
                />
              </div>
            </Elements>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default PaymentPage;
